<script setup lang="ts">
import NewDateRangePicker from '@/components/Globals/Inputs/NewDateRangePicker.vue'
import SearchBarField from '@/components/Globals/SearchBarField.vue'
import IcAnimateSpin from '@/components/Icons/IcAnimateSpin.vue'
import IcSearch from '@/components/Icons/IcSearch.vue'
import MaintenanceReportSearchBarSchema from '@/schemas/maintenance/MaintenanceReportSearchBarSchema'
import { useMaintenanceStore } from '@/store/maintenance'
import { formatPeriodDate } from '@/utils/date/date-time'
import { Form, Field } from 'vee-validate'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const isLoading = ref(false)
const { fetchMaintenanceReport } = useMaintenanceStore()

interface IMaintenanceReportSearchForm {
  period: Date[]
}

async function onSubmit(values: IMaintenanceReportSearchForm) {
  isLoading.value = true
  try {
    const formatted = formatPeriodDate(values.period)
    const [startPart, endPart] = formatted.split('&')
    const startDate = startPart.split('=')[1]
    const endDate = endPart.split('=')[1]
    await fetchMaintenanceReport(startDate, endDate)
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <div class="mt-4 bg-white p-4 shadow-md">
    <Form :validation-schema="MaintenanceReportSearchBarSchema" @submit="onSubmit" class="flex w-full flex-col gap-4">
      <div class="flex flex-wrap items-end gap-3">
        <SearchBarField :label="t('maintenance_report.header.actions.search.fields.period')" class="min-w-[280px] flex-1">
          <Field name="period" v-slot="{ field }">
            <NewDateRangePicker
              v-bind="field"
              :placeholder="t('plugins.vue_datepicker.placeholder')"
              name="period"
              class="w-full" />
          </Field>
        </SearchBarField>

        <button
          type="submit"
          :disabled="isLoading"
          class="flex min-h-[48px] flex-row items-center justify-center bg-blue px-4 py-3 text-sm text-white hover:bg-blue-2 disabled:cursor-not-allowed disabled:opacity-60">
          <IcSearch v-if="!isLoading" class="mr-1 h-4 w-4" />
          <span v-else class="mr-1 animate-spin">
            <IcAnimateSpin class="h-4 w-4 text-white" />
          </span>
          {{ t('maintenance_report.header.buttons.search') }}
        </button>
      </div>
    </Form>
  </div>
</template>
